const answerToText = (answer, booleanLabels) => {
  if (!answer || typeof answer !== 'object') {
    return ''
  }

  switch (answer.type) {
    case 'number':
      return String(answer.number ?? '')
    case 'phone_number':
      return String(answer.phone_number ?? '')
    case 'date':
      return String(answer.date ?? '')
    case 'boolean':
      return answer.boolean ? booleanLabels.yes : booleanLabels.no
    case 'text':
      return String(answer.text ?? '')
    case 'choices':
      if (Array.isArray(answer.choices)) {
        return answer.choices.map((choice) => choice.label ?? '').filter((label) => label).join(', ')
      }
      return ''
    default:
      return ''
  }
}

const formatResponseTime = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  const pad = (value) => (value < 10 ? `0${value}` : String(value))

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(rest)}`
  }

  return `${minutes}:${pad(rest)}`
}

export const findComponentByRef = (components, ref) => {
  if (!Array.isArray(components)) {
    return null
  }

  for (const component of components) {
    if (!component || typeof component !== 'object') {
      continue
    }
    if (component.ref === ref) {
      return component
    }
    const nested = findComponentByRef(component.components, ref)
    if (nested) {
      return nested
    }
  }

  return null
}

export const unwrapCompareAnswer = (raw) => {
  // Author text is escaped before tokens are parsed, so undo that to compare against the raw answer.
  let expected = String(raw ?? '')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()
  const wrappedInSingle = expected.startsWith("'") && expected.endsWith("'") && expected.length >= 2
  const wrappedInDouble = expected.startsWith('"') && expected.endsWith('"') && expected.length >= 2
  if (wrappedInSingle || wrappedInDouble) {
    expected = expected.slice(1, -1)
  }
  return expected
}

// Older saves ran slice(1, -1) on an unquoted label, so "Kapelusz" was stored as "apelus".
export const resolveCompareExpected = (expected, ref, components) => {
  const component = findComponentByRef(components, ref)
  const labels = Array.isArray(component?.choices)
    ? component.choices
      .map((choice) => (choice && choice.label != null ? String(choice.label) : ''))
      .filter((label) => label !== '')
    : []

  if (labels.includes(expected)) {
    return expected
  }

  const repaired = labels.filter((label) => label.length > 2 && label.slice(1, -1) === expected)
  if (repaired.length === 1) {
    return repaired[0]
  }

  return expected
}

/*
 * Quiz compare tokens. For a choice question the token names the correct choice:
 * {{compare:QREF|ref:CHOICEREF}}, CHOICEREF being the choice's ref (its id for a choice
 * without one). Answers keep the ref and id of the selected choices, and a translation keeps
 * the refs while it renames the choices, so the comparison works in every language. Other
 * questions keep the expected text: {{compare:QREF|'text'}}, as older saves of choice
 * questions do with the label of the correct choice. app/Services/CompareTokenUpgrader.php
 * follows the same rules.
 */
export const COMPARE_CHOICE_TYPES = ['multiple_choice', 'picture_choice', 'dropdown']

const COMPARE_REF_PREFIX = 'ref:'

const hasValue = (value) => value !== undefined && value !== null && String(value) !== ''

export const isCompareChoiceQuestion = (component) => Boolean(component)
  && COMPARE_CHOICE_TYPES.includes(component.type)
  && Array.isArray(component.choices)

// What a compare token stores for a choice: its ref, or its id when it has no ref.
export const compareChoiceKey = (choice) => {
  if (!choice || typeof choice !== 'object') {
    return ''
  }
  if (hasValue(choice.ref)) {
    return String(choice.ref)
  }
  return hasValue(choice.id) ? String(choice.id) : ''
}

export const compareRefToken = (questionRef, choiceKey) => `{{compare:${questionRef}|${COMPARE_REF_PREFIX}${choiceKey}}}`

const objectChoices = (component) => (Array.isArray(component?.choices)
  ? component.choices.filter((choice) => choice && typeof choice === 'object')
  : [])

export const findCompareChoice = (component, choiceKey) => {
  if (!hasValue(choiceKey)) {
    return null
  }
  const key = String(choiceKey)
  const choices = objectChoices(component)
  return choices.find((choice) => hasValue(choice.ref) && String(choice.ref) === key)
    ?? choices.find((choice) => hasValue(choice.id) && String(choice.id) === key)
    ?? null
}

/**
 * The text of a compare token after "compare:" -> { ref, choiceKey, expected }: choiceKey for
 * the ref format (expected null), expected (unwrapped) for a text (choiceKey null).
 */
export const parseCompareToken = (body) => {
  const parts = String(body ?? '').split('|')
  const ref = (parts[0] ?? '').replace(/\s+/g, '')
  const value = parts.slice(1).join('|')
  const trimmed = value.trim()

  if (trimmed.startsWith(COMPARE_REF_PREFIX)) {
    return { ref, choiceKey: trimmed.slice(COMPARE_REF_PREFIX.length).trim(), expected: null }
  }

  return { ref, choiceKey: null, expected: unwrapCompareAnswer(value) }
}

// The one choice with this label, or the one the repaired-label rule gives; null when none or several.
const choiceForLabel = (component, expected) => {
  const labelled = objectChoices(component).filter((choice) => hasValue(choice.label) && compareChoiceKey(choice) !== '')
  const exact = labelled.filter((choice) => String(choice.label) === expected)
  if (exact.length > 0) {
    return exact.length === 1 ? exact[0] : null
  }

  const repaired = labelled.filter((choice) => {
    const label = String(choice.label)
    return label.length > 2 && label.slice(1, -1) === expected
  })
  return repaired.length === 1 ? repaired[0] : null
}

/**
 * The correct choice of a compare token on a choice question: { ref, component, choice, key },
 * key being what the ref format stores. A token in the older label format resolves when exactly
 * one choice has that label (or the repaired label). Null for a text compare, a question that is
 * gone or no longer a choice question, and a choice that cannot be found.
 */
export const resolveCompareChoice = (body, components) => {
  const { ref, choiceKey, expected } = parseCompareToken(body)
  const component = findComponentByRef(components, ref)
  if (!isCompareChoiceQuestion(component)) {
    return null
  }

  const choice = choiceKey !== null ? findCompareChoice(component, choiceKey) : choiceForLabel(component, expected)
  if (!choice) {
    return null
  }

  return { ref, component, choice, key: choiceKey !== null ? choiceKey : compareChoiceKey(choice) }
}

const sameChoice = (selected, expected) => {
  if (hasValue(selected.ref) && hasValue(expected.ref)) {
    return String(selected.ref) === String(expected.ref)
  }
  if (hasValue(selected.id) && hasValue(expected.id)) {
    return String(selected.id) === String(expected.id)
  }
  return hasValue(selected.label) && String(selected.label) === String(expected.label ?? '')
}

/**
 * Whether the answer is exactly the expected choice. Multiple and picture choice answers hold the
 * selected choices with their ref and id ("Other" has ref and id "other"); a dropdown answer is
 * the text of the selected label, which comes from the same (translated) choices as the expected one.
 */
const isExpectedChoiceSelected = (answer, expected) => {
  if (answer.type === 'choices') {
    const selected = Array.isArray(answer.choices)
      ? answer.choices.filter((choice) => choice && typeof choice === 'object')
      : []
    return selected.length === 1 && sameChoice(selected[0], expected)
  }

  if (answer.type === 'text') {
    return hasValue(expected.label) && String(answer.text ?? '') === String(expected.label)
  }

  return false
}

const compareHtml = (isCorrect, value, expected) => {
  const safeExpected = escapeText(expected)
  if (isCorrect) {
    return `<span class="quiz-compare"><i class="icon-yes"></i> ${safeExpected}</span>`
  }

  return `<span class="quiz-compare"><i class="icon-close"></i> <strike>${escapeText(value)}</strike> <i class="icon-right"></i>${safeExpected}</span>`
}

const stripAnchorsAndBareUrls = (html) => {
  const images = []
  let output = html.replace(/<img\b[^>]*>/gi, (image) => {
    const token = `\u0000IMG${images.length}\u0000`
    images.push(image)
    return token
  })

  output = output.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1')
  output = output.replace(/\b(?:https?:\/\/|www\.)[^\s<>'"]+/gi, '')
  output = output.replace(/(^|[\s('"=])\/\/[^\s<>'"]+/g, '$1')

  images.forEach((image, index) => {
    output = output.replace(`\u0000IMG${index}\u0000`, image)
  })

  return output
}

// Values piped in from the URL, answers or variables are text, never markup.
// Private-use range holding escaped markdown characters while the rules run (see pipeText).
const LITERAL_BASE = 0xe000
const LITERAL_CHARS = /[\ue000-\ue07f]/g

const escapeText = (value) => String(value).replace(/</g, '&lt;').replace(/>/g, '&gt;')

const escapeAttr = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/'/g, '&#39;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')

/**
 * Returns the URL when its scheme is allowed, otherwise null. Browsers drop
 * whitespace and control characters inside a scheme ("java\tscript:"), so the
 * check runs on a copy without them.
 */
const safeUrl = (raw, allowedSchemes) => {
  const url = String(raw).trim()
  const probe = url.replace(/[\u0000-\u0020\u007f]/g, '').toLowerCase()
  const scheme = probe.match(/^([a-z][a-z0-9+.-]*):/)

  if (!scheme) {
    return url
  }

  return allowedSchemes.includes(scheme[1]) ? url : null
}

const IMAGE_SCHEMES = ['http', 'https']
const LINK_SCHEMES = ['http', 'https', 'mailto', 'tel']

/**
 * Substitute hidden fields, answers and variables, then render markdown.
 * Basic-plan forms pass allowLinks: false so split fragments cannot become a link.
 */
export const pipeText = (text, context = {}, options = {}) => {
  if (!text) {
    return ''
  }

  const allowLinks = options.allowLinks !== false
  const booleanLabels = options.booleanLabels ?? { yes: 'Yes', no: 'No' }
  const hidden = context.hidden ?? {}
  const answers = context.answers ?? {}
  const variables = context.variables ?? {}

  // Author text is markdown. Raw tags are dropped (as the legacy renderer always
  // did) and stray angle brackets are escaped, so only markdown can produce HTML.
  let result = String(text)
    .replace(/<(?:\/?[a-z][^>]*|!--[\s\S]*?--|![^>]*|\?[^>]*)>/gi, '')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  result = result.replace(/\{\{component:(.*?)\}\}/gi, (_, token) => {
    const key = token.trim()
    if (answers[key]) {
      return escapeText(answerToText(answers[key], booleanLabels))
    }
    return ''
  })

  result = result.replace(/\{\{compare:(.*?)\}\}/gi, (_, token) => {
    const { ref, choiceKey, expected } = parseCompareToken(token)
    if (!ref || !answers[ref]) {
      return ''
    }

    const value = answerToText(answers[ref], booleanLabels)

    // The components are the ones the respondent sees, so the label is in their language.
    if (choiceKey !== null) {
      const choice = findCompareChoice(findComponentByRef(context.components, ref), choiceKey)
      if (!choice) {
        return ''
      }
      return compareHtml(isExpectedChoiceSelected(answers[ref], choice), value, String(choice.label ?? ''))
    }

    const resolved = resolveCompareExpected(expected, ref, context.components)
    return compareHtml(value === resolved, value, resolved)
  })

  result = result.replace(/\{\{hidden:(.*?)\}\}/gi, (_, token) => {
    const key = token.trim()
    if (hidden[key] && hidden[key].value !== undefined) {
      return escapeText(hidden[key].value)
    }
    return ''
  })

  result = result.replace(/\{\{variable:(.*?)\}\}/gi, (_, token) => {
    const key = token.trim()
    if (variables[key] !== undefined) {
      return escapeText(variables[key])
    }
    return ''
  })

  if (context.responseToken) {
    result = result.replace(/\{\{response:id\}\}/gi, escapeText(context.responseToken))
  }

  const responseTime = context.responseTime
  const hasResponseTime = typeof responseTime === 'number' && Number.isFinite(responseTime)
  result = result.replace(
    /\{\{response:time\}\}/gi,
    hasResponseTime ? formatResponseTime(responseTime) : '',
  )
  result = result.replace(
    /\{\{response:seconds\}\}/gi,
    hasResponseTime ? String(Math.max(0, Math.round(responseTime))) : '',
  )

  if (options.bareTokens) {
    result = result.replace(/\{\{([^}:]+)\}\}/g, (_, key) => {
      const trimmedKey = key.trim()
      if (variables[trimmedKey] !== undefined) {
        return escapeText(variables[trimmedKey])
      }
      if (hidden[trimmedKey] && hidden[trimmedKey].value !== undefined) {
        return escapeText(hidden[trimmedKey].value)
      }
      if (answers[trimmedKey] !== undefined) {
        return escapeText(answerToText(answers[trimmedKey], booleanLabels))
      }
      return ''
    })
  }

  result = result.replace(/(?:\r\n|\r|\n)/g, '<br>')

  // The editor saves markdown through turndown, which escapes characters the author typed
  // ("\*Parking", "1\. Parking", "\*\*"). Swap each escaped character for a private-use
  // one so the markdown rules skip it, and put the character back afterwards.
  // Characters of that range already in the text or a piped value are kept as entities, so
  // only the ones made here turn back into characters ("\ue03cimg" must not become "<img").
  result = result.replace(LITERAL_CHARS, (char) => `&#${char.charCodeAt(0)};`)
  result = result.replace(/\\([\\`*_{}\[\]()#+\-.!>~=|])/g, (_, char) =>
    String.fromCharCode(LITERAL_BASE + char.charCodeAt(0)),
  )

  const rules = [
    {
      regex: /!\[([^\[]+)\]\(([^\)]+)\)/g,
      replacement: (_, alt, src) => {
        const url = safeUrl(src, IMAGE_SCHEMES)
        return url === null ? alt : `<img src='${escapeAttr(url)}' alt='${escapeAttr(alt)}'>`
      },
    },
  ]

  if (allowLinks) {
    rules.push({
      regex: /\[([^\[]+)\]\(([^\)]+)\)/g,
      replacement: (_, label, href) => {
        const url = safeUrl(href, LINK_SCHEMES)
        return url === null
          ? label
          : `<a href='${escapeAttr(url)}' target="_blank" rel="nofollow noreferrer noopener">${label}</a>`
      },
    })
  }

  rules.push(
    { regex: /(\*\*\*)(.*?)\1/g, replacement: '<strong><em>$2</em></strong>' },
    { regex: /(\*\*)(.*?)\1/g, replacement: '<strong>$2</strong>' },
    // *italic* also inside a word ("for*m tit*le"), as the editor writes it; "5 * 3 * 2" stays as is
    { regex: /\*([^\s*](?:[^*\n]*[^\s*])?)\*/g, replacement: '<em>$1</em>' },
    { regex: /\~\~(.*?)\~\~/g, replacement: '<del>$1</del>' },
    { regex: /\\/g, replacement: '' },
  )

  let processed = `\n${result}\n`
  rules.forEach((rule) => {
    processed = processed.replace(rule.regex, rule.replacement)
  })
  processed = processed
    .replace(LITERAL_CHARS, (char) => String.fromCharCode(char.charCodeAt(0) - LITERAL_BASE))
    .trim()

  if (!allowLinks) {
    processed = stripAnchorsAndBareUrls(processed)
  }

  return processed
}
